import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarHeart, MessageCircle, CheckCircle2, XCircle, Sun, CloudRain, Snowflake, Sparkles, AlertCircle, AlertTriangle, HelpCircle, Utensils, Microscope, Leaf } from "lucide-react";
import { BLOG, SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const seasonIconMap = { Sun, CloudRain, Snowflake, Utensils };

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG.find((b) => b.slug === slug);

  useSEO({
    title: post ? (post.metaTitle || post.title) : "Article",
    description: post ? (post.metaDescription || post.excerpt) : "Article",
    path: `/blog/${slug}`,
  });

  if (!post) {
    return (
      <section className="pt-40 pb-20 na-container text-center">
        <h1 className="text-4xl">Article not found</h1>
        <Link to="/blog" className="btn-primary mt-6">Back to Blog</Link>
      </section>
    );
  }

  const waLink = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(`Hello Dr. Anita, I read your article "${post.title}" and would like to book a consultation.`)}`;

  return (
    <>
      <section className="pt-32 pb-8" data-testid={`blogpost-${slug}`}>
        <div className="na-container max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[color:var(--na-muted)] hover:text-[color:var(--na-teal)]">
            <ArrowLeft size={14} /> Back to Blog
          </Link>
          <div className="mt-6 flex items-center gap-3 text-xs text-[color:var(--na-muted)] flex-wrap">
            <span className="px-2.5 py-1 rounded-full bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] font-semibold">{post.tag}</span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.read}</span>
            {post.fromOwner && (
              <span className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[color:var(--na-maroon)]/10 text-[color:var(--na-maroon)] font-semibold">
                <Sparkles size={12} /> By Dr. Anita
              </span>
            )}
          </div>
          <h1 className="mt-4 text-4xl sm:text-5xl leading-tight">{post.title}</h1>
        </div>
      </section>

      <section className="pb-10">
        <div className="na-container max-w-4xl">
          <img src={post.cover} alt={post.title} className="w-full rounded-[2rem] aspect-[16/9] object-cover" />
        </div>
      </section>

      <section className="pb-20">
        <div className="na-container max-w-3xl">
          <div className="space-y-6">
            {Array.isArray(post.body) && post.body.map((block, i) => renderBlock(block, i))}
          </div>

          <div className="mt-14 na-card p-8 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
            <div>
              <h3 className="text-2xl">Have questions?</h3>
              <p className="mt-1 text-sm text-[color:var(--na-muted)]">Speak with Dr. Anita and start your personalized care plan.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/book" className="btn-primary">
                <CalendarHeart size={16} /> Book Appointment
              </Link>
              <a href={waLink} target="_blank" rel="noreferrer" className="btn-outline">
                <MessageCircle size={16} /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function renderBlock(block, i) {
  // Legacy string paragraphs (backwards compat)
  if (typeof block === "string") {
    return <p key={i} className="text-lg text-[color:var(--na-text)] leading-[1.85]">{block}</p>;
  }

  switch (block.type) {
    case "p":
      return <p key={i} className="text-lg text-[color:var(--na-text)] leading-[1.85]">{block.text}</p>;

    case "h2":
      return (
        <h2 key={i} className="mt-12 mb-2 text-3xl md:text-4xl leading-tight">
          <span className="gold-underline">{block.text}</span>
        </h2>
      );

    case "h3":
      return <h3 key={i} className="mt-8 mb-2 text-2xl">{block.text}</h3>;

    case "food":
      return (
        <div key={i} className="na-card p-6 md:p-7 flex gap-5" data-testid={`food-${block.num}`}>
          <div className="shrink-0 w-12 h-12 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center font-display text-2xl font-semibold">
            {block.num}
          </div>
          <div className="flex-1">
            <h3 className="text-2xl">{block.name}</h3>
            <p className="mt-2 text-[color:var(--na-muted)] leading-relaxed">{block.desc}</p>
            {block.bullets && (
              <ul className="mt-3 space-y-1.5">
                {block.bullets.map((b, k) => (
                  <li key={k} className="flex items-start gap-2 text-[color:var(--na-muted)] text-sm">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--na-teal)] mt-2.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
            {block.tag && (
              <div className="mt-4 rounded-xl bg-[color:var(--na-gold)]/10 border border-[color:var(--na-gold)]/30 px-3.5 py-2.5 text-sm">
                <span className="font-semibold text-[color:var(--na-gold)]">{block.tag}: </span>
                <span className="text-[color:var(--na-text)]">{block.tagText}</span>
              </div>
            )}
          </div>
        </div>
      );

    case "ul":
      return (
        <ul key={i} className="space-y-2.5">
          {block.items.map((it, j) => (
            <li key={j} className="flex items-start gap-3 text-lg text-[color:var(--na-text)] leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--na-teal)] mt-3 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );

    case "warn_list":
      return (
        <div key={i} className="grid gap-3 sm:grid-cols-2">
          {block.items.map((it, j) => (
            <div key={j} className="rounded-2xl bg-[color:var(--na-maroon)]/5 border border-[color:var(--na-maroon)]/20 p-5 flex gap-3">
              <AlertTriangle size={20} className="text-[color:var(--na-maroon)] shrink-0 mt-0.5" />
              <p className="text-sm text-[color:var(--na-text)] leading-relaxed">{it}</p>
            </div>
          ))}
        </div>
      );

    case "faq":
      return (
        <div key={i} className="na-card p-6">
          <div className="flex gap-3 items-start">
            <div className="w-8 h-8 rounded-full bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center shrink-0 mt-1">
              <HelpCircle size={16} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg md:text-xl">{block.q}</h3>
              <p className="mt-2 text-[color:var(--na-muted)] leading-relaxed">{block.a}</p>
            </div>
          </div>
        </div>
      );

    case "avoid_list":
      return (
        <div key={i} className="grid gap-3 sm:grid-cols-2">
          {block.items.map((it, j) => (
            <div key={j} className="na-card p-5 flex gap-3">
              <XCircle size={22} className="text-[color:var(--na-maroon)] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{it.name}</p>
                <p className="mt-1 text-sm text-[color:var(--na-muted)] leading-relaxed">{it.desc}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "routine_list":
      return (
        <div key={i} className="grid gap-3 sm:grid-cols-2">
          {block.items.map((it, j) => (
            <div key={j} className="na-card p-5 flex gap-3">
              <CheckCircle2 size={22} className="text-[color:var(--na-teal)] shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">{it.name}</p>
                <p className="mt-1 text-sm text-[color:var(--na-muted)] leading-relaxed">{it.desc}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case "season": {
      const Icon = seasonIconMap[block.icon] || Sun;
      return (
        <div key={i} className="na-card p-6 md:p-7">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[color:var(--na-gold)]/15 text-[color:var(--na-gold)] flex items-center justify-center">
              <Icon size={20} />
            </div>
            <h3 className="text-2xl">{block.name}</h3>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[color:var(--na-teal)]">Eat more</p>
              <ul className="mt-2 space-y-1.5">
                {block.eat.map((e, k) => (
                  <li key={k} className="flex items-start gap-2 text-[color:var(--na-text)]">
                    <CheckCircle2 size={16} className="text-[color:var(--na-teal)] shrink-0 mt-1" />
                    <span>{e}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-[color:var(--na-maroon)]">Avoid</p>
              <ul className="mt-2 space-y-1.5">
                {block.avoid.map((a, k) => (
                  <li key={k} className="flex items-start gap-2 text-[color:var(--na-text)]">
                    <XCircle size={16} className="text-[color:var(--na-maroon)] shrink-0 mt-1" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      );
    }

    case "tips":
      return (
        <div key={i} className="na-card p-6">
          <h3 className="text-xl">{block.name}</h3>
          <ul className="mt-3 space-y-2">
            {block.items.map((it, j) => (
              <li key={j} className="flex items-start gap-2 text-[color:var(--na-muted)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--na-teal)] mt-2.5 shrink-0" />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case "myth":
      return (
        <div key={i} className="na-card p-6 md:p-8" data-testid={`myth-${block.num}`}>
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-[color:var(--na-maroon)] text-white font-display text-2xl font-semibold flex items-center justify-center shadow-[0_10px_24px_-14px_rgba(180,58,84,0.6)]">
              {block.num}
            </div>
            <div className="pt-1">
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--na-maroon)] font-semibold">Myth {block.num}</div>
              <h3 className="text-2xl md:text-3xl mt-1 leading-tight">{block.title}</h3>
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <MythSection label="Fact" Icon={CheckCircle2} tone="teal" items={block.fact} />
            {block.scientific && (
              <MythSection label="Scientific Explanation" Icon={Microscope} tone="neutral" items={block.scientific} />
            )}
            {block.ayurvedic && (
              <MythSection label="Ayurvedic Perspective" Icon={Leaf} tone="gold" items={block.ayurvedic} />
            )}
          </div>
        </div>
      );

    case "sign":
      return (
        <div key={i} className="na-card p-6 md:p-8" data-testid={`sign-${block.num}`}>
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-14 h-14 rounded-2xl bg-[color:var(--na-teal)]/10 text-[color:var(--na-teal)] flex items-center justify-center font-display text-2xl font-semibold">
              {block.num}
            </div>
            <div className="pt-1">
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--na-teal)] font-semibold">Sign {block.num}</div>
              <h3 className="text-2xl md:text-3xl mt-1 leading-tight">{block.title}</h3>
            </div>
          </div>
          {block.intro && <p className="mt-5 text-[color:var(--na-muted)] leading-relaxed">{block.intro}</p>}
          <div className="mt-6 space-y-5">
            {block.sections.map((sec, j) => {
              const isAyu = /ayurved/i.test(sec.label);
              const isTip = /(tip|natural)/i.test(sec.label);
              const borderCls = isAyu ? "border-[color:var(--na-gold)]" : isTip ? "border-[color:var(--na-teal)]" : "border-[color:var(--na-maroon)]/40";
              const labelCls = isAyu ? "text-[color:var(--na-gold)]" : isTip ? "text-[color:var(--na-teal)]" : "text-[color:var(--na-maroon)]";
              return (
                <div key={j} className={`border-l-2 ${borderCls} pl-4`}>
                  <div className={`text-xs uppercase tracking-[0.22em] font-semibold ${labelCls}`}>{sec.label}</div>
                  {sec.text && <p className="mt-2 text-[color:var(--na-text)] leading-relaxed">{sec.text}</p>}
                  {sec.items && (
                    <ul className="mt-2 space-y-1.5">
                      {sec.items.map((it, k) => (
                        <li key={k} className="flex items-start gap-2 text-[color:var(--na-muted)]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--na-teal)] mt-2.5 shrink-0" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );


    case "disclaimer":
      return (
        <div key={i} className="mt-10 rounded-2xl border border-dashed border-[color:var(--na-maroon)]/40 bg-[color:var(--na-maroon)]/5 p-5 flex gap-3">
          <AlertCircle size={20} className="text-[color:var(--na-maroon)] shrink-0 mt-0.5" />
          <p className="text-sm text-[color:var(--na-text)] leading-relaxed">
            <span className="font-semibold">Disclaimer: </span>{block.text}
          </p>
        </div>
      );

    default:
      return null;
  }
}

function MythSection({ label, Icon, tone, items }) {
  const tones = {
    teal:    { bg: "bg-[color:var(--na-teal)]/5",  border: "border-[color:var(--na-teal)]/25",  icon: "text-[color:var(--na-teal)]",  labelColor: "text-[color:var(--na-teal)]" },
    gold:    { bg: "bg-[color:var(--na-gold)]/8",  border: "border-[color:var(--na-gold)]/30",  icon: "text-[color:var(--na-gold)]",  labelColor: "text-[color:var(--na-gold)]" },
    neutral: { bg: "bg-[color:var(--na-section)]", border: "border-[color:var(--na-border)]",   icon: "text-[color:var(--na-muted)]", labelColor: "text-[color:var(--na-muted)]" },
  };
  const t = tones[tone] || tones.neutral;
  const list = Array.isArray(items) ? items : [items];

  return (
    <div className={`rounded-2xl ${t.bg} border ${t.border} p-5`}>
      <div className={`flex items-center gap-2 ${t.labelColor} text-xs uppercase tracking-[0.22em] font-semibold`}>
        <Icon size={16} className={t.icon} />
        <span>{label}</span>
      </div>
      <div className="mt-3 space-y-3">
        {list.map((it, k) => {
          if (typeof it === "string") {
            return <p key={k} className="text-[color:var(--na-text)] leading-relaxed">{it}</p>;
          }
          if (it && it.bullets) {
            return (
              <ul key={k} className="space-y-1.5">
                {it.bullets.map((b, m) => (
                  <li key={m} className="flex items-start gap-2 text-[color:var(--na-muted)]">
                    <span className="w-1.5 h-1.5 rounded-full bg-current mt-2.5 shrink-0 opacity-60" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}
